import type {
  ClickableScreenScene,
  ClickableScreenZone,
  CommandItem,
  PedaCategoryKey,
  Scene,
} from '../../types';

export interface ClickableZoneConfig {
  id: string;
  label: string;
  position: ClickableScreenZone['position'];
  goto: string;
  pedaShields: number;
  pedaCategory?: PedaCategoryKey;
  condition: string;
  actions: CommandItem[];
}

export interface ClickableScreenEditorConfig {
  instruction: string;
  zones: ClickableZoneConfig[];
}

export function defaultClickableScreenConfig(): ClickableScreenEditorConfig {
  return {
    instruction: '',
    zones: [],
  };
}

export function parseClickableScreenScene(scene: Scene): ClickableScreenEditorConfig {
  if (scene.type !== 'clickableScreen') {
    return defaultClickableScreenConfig();
  }
  return {
    instruction: scene.instruction ?? '',
    zones: (scene.zones ?? []).map((zone) => ({
      id: zone.id,
      label: zone.label,
      position: zone.position,
      goto: zone.goto,
      pedaShields: zone.pedaShields ?? 0,
      pedaCategory: zone.pedaCategory,
      condition: zone.condition ?? '',
      actions: zone.actions ?? [],
    })),
  };
}

export function applyClickableScreenScene(scene: Scene, config: ClickableScreenEditorConfig): ClickableScreenScene {
  const zones: ClickableScreenZone[] = config.zones.map((zone) => {
    const next: ClickableScreenZone = {
      id: zone.id,
      label: zone.label,
      position: zone.position,
      goto: zone.goto,
    };
    if (zone.pedaShields > 0) {
      next.pedaShields = zone.pedaShields;
      if (zone.pedaCategory) next.pedaCategory = zone.pedaCategory;
    }
    if (zone.condition.trim()) next.condition = zone.condition.trim();
    if (zone.actions.length > 0) next.actions = zone.actions;
    return next;
  });

  return {
    id: scene.id,
    type: 'clickableScreen',
    background: scene.background,
    ...(scene.backgroundLoop !== undefined ? { backgroundLoop: scene.backgroundLoop } : {}),
    condition: scene.condition,
    instruction: config.instruction.trim() || undefined,
    zones,
  };
}

export function initializeClickableScreenScene(scene: Scene): ClickableScreenScene {
  if (scene.type === 'clickableScreen') return scene;
  return applyClickableScreenScene(scene, defaultClickableScreenConfig());
}
